import api from './api.service';
import type { RejectionPayload } from './rejection.service';

export type PaymentType =
  | 'SupplierBillPayment'
  | 'SupplierAdvance'
  | 'ExpensePayment'
  | 'DirectPayment'
  | 'BankTransfer'
  | 'CashTransfer'
  | 'AccountTransfer'
  | 'EmployeePayment';
export type PaymentMode = 'Cash' | 'Cheque' | 'BankTransfer' | 'Online' | 'Card' | 'PDC' | 'Other';
export type PaymentSettlementScope = 'SingleSupplier' | 'MultiSupplier';

export interface PaymentVoucherAllocation {
  id?: string;
  supplier_invoice_id: string;
  /** Snapshot of invoice supplier — used for MultiSupplier list/print grouping. */
  supplier_id?: string | null;
  allocated_amount: number;
  supplierInvoice?: {
    id: string;
    invoice_number: string;
    supplier_invoice_number?: string;
    supplier_id?: string;
    balance_due?: number;
    grand_total?: number;
    paid_amount?: number;
  };
}

export interface PaymentVoucherDirectLine {
  id?: string;
  account_id: string;
  amount: number;
  description?: string;
  project_id?: string;
  cost_center?: string;
  account?: { id: string; account_code: string; account_name: string };
  project?: { id: string; project_number?: string; project_name?: string };
}

export interface PaymentVoucherChargeLine {
  id?: string;
  account_id: string;
  amount: number;
  description?: string;
  project_id?: string;
  cost_center?: string;
  account?: { id: string; account_code: string; account_name: string };
  project?: { id: string; project_number?: string; project_name?: string };
}

export interface PaymentVoucher {
  id: string;
  company_id: string;
  voucher_number: string;
  voucher_date: string;
  posting_date?: string;
  payment_type: PaymentType;
  /** SingleSupplier (default) | MultiSupplier for SupplierBillPayment. */
  settlement_scope?: PaymentSettlementScope | string;
  supplier_id?: string;
  employee_id?: string | null;
  leave_settlement_id?: string | null;
  payee_name?: string;
  payment_account_id: string;
  destination_account_id?: string;
  amount: number;
  currency: string;
  payment_mode: PaymentMode;
  reference_number?: string;
  narration?: string;
  cheque_number?: string;
  cheque_date?: string;
  bank_name?: string;
  transaction_reference?: string;
  status: 'Draft' | 'Submitted' | 'Approved' | 'Posted' | 'Cancelled';
  approval_instance_id?: string | null;
  journal_voucher_id?: string;
  project_id?: string;
  department_id?: string | null;
  cost_center?: string;
  purchase_order_id?: string;
  /** Enriched on detail — business PO number for display (never show purchase_order_id). */
  purchase_order_number?: string | null;
  po_milestone_id?: string;
  /** Enriched on detail — e.g. "MS-1 — Advance". */
  po_milestone_label?: string | null;
  supplier_advance_request_id?: string;
  posted_at?: string;
  posted_by?: string;
  supplier?: { id: string; supplier_name: string; supplier_code?: string };
  paymentAccount?: { id: string; account_code: string; account_name: string };
  destinationAccount?: { id: string; account_code: string; account_name: string };
  allocations?: PaymentVoucherAllocation[];
  advanceAllocations?: Array<{
    id?: string;
    supplier_advance_request_id: string;
    allocated_amount: number;
    supplierAdvanceRequest?: {
      id: string;
      advance_request_number?: string;
      purchase_order_id?: string;
    };
  }>;
  directLines?: PaymentVoucherDirectLine[];
  chargeLines?: PaymentVoucherChargeLine[];
  discount_amount?: number;
  write_off_amount?: number;
  settlement_remarks?: string;
  settlement_snapshot?: SettlementLineInput[];
}

export type SettlementAdvanceAdj = { payment_voucher_id: string; amount: number };
export type SettlementDebitNoteAdj = { supplier_debit_note_id: string; amount: number };

export type SettlementLineInput = {
  supplier_invoice_id: string;
  cash_amount: number;
  advance_adjustments?: SettlementAdvanceAdj[];
  debit_note_adjustments?: SettlementDebitNoteAdj[];
};

export interface ApSettlementInvoiceRow {
  id: string;
  invoice_number: string;
  supplier_id?: string;
  supplier_invoice_date?: string;
  due_date?: string;
  grand_total: number;
  paid_amount: number;
  balance_due: number;
  currency?: string;
  days_overdue?: number;
  payment_status?: string;
  advance_adjusted_amount?: number;
  debit_note_adjusted_amount?: number;
  invoice_type?: string;
  /** Header notes, or line descriptions when the bill has no notes. */
  narration?: string;
}

export interface ApSettlementContext {
  supplier_id: string;
  currency?: string | null;
  invoices: ApSettlementInvoiceRow[];
  advances: Array<{
    id: string;
    voucher_number: string;
    purchase_order_number?: string;
    open_balance: number;
    currency?: string;
  }>;
  debit_notes: Array<{
    id: string;
    debit_note_number: string;
    debit_note_date?: string;
    open_balance: number;
    currency?: string;
  }>;
  /** Invoices / DPIs for the supplier not yet posted to the GL, so not payable yet. */
  awaiting_posting?: Array<{
    id: string;
    invoice_number: string;
    is_direct: boolean;
    supplier_invoice_number?: string | null;
    invoice_status: string;
    approval_status?: string | null;
    grand_total: number;
    currency: string;
  }>;
}

export interface ApSettlementContextMulti {
  settlement_scope: 'MultiSupplier';
  supplier_ids: string[];
  currency?: string | null;
  invoices: ApSettlementInvoiceRow[];
  advances: unknown[];
  debit_notes: unknown[];
  awaiting_posting?: Array<{
    id: string;
    invoice_number: string;
    is_direct: boolean;
    supplier_invoice_number?: string | null;
    invoice_status: string;
    approval_status?: string | null;
    grand_total: number;
    currency: string;
    supplier_id?: string;
  }>;
}

export interface PaymentVoucherAdvanceApplication {
  id?: string;
  supplier_invoice_id: string;
  invoice_number: string;
  narration?: string;
  supplier_invoice_number?: string | null;
  allocated_amount: number;
  adjustment_date: string;
  currency: string;
}

export interface PaymentVoucherAllocationLine {
  supplier_invoice_id: string;
  invoice_number: string;
  narration?: string;
  supplier_invoice_number?: string;
  due_date?: string;
  grand_total?: number;
  balance_due?: number;
  payment_status?: string;
  cash_amount: number;
  advance_adjustment: number;
  debit_note_adjustment: number;
  line_total: number;
  advance_details?: Array<{
    payment_voucher_id: string;
    advance_voucher_number?: string;
    amount: number;
  }>;
  debit_note_details?: Array<{
    supplier_debit_note_id: string;
    debit_note_number?: string;
    amount: number;
  }>;
}

export interface PaymentVoucherSettlementHistory {
  payment_voucher: {
    id: string;
    voucher_number: string;
    status: string;
    amount: number;
    discount_amount: number;
    write_off_amount: number;
    settlement_remarks: string | null;
  };
  summary: {
    total_invoice_selected: number;
    total_advance_adjustment: number;
    total_debit_note_adjustment: number;
    discount_amount: number;
    write_off_amount: number;
    net_cash_payable: number;
  };
  allocation_lines: PaymentVoucherAllocationLine[];
  cash_allocations: Array<{ supplier_invoice_id: string; invoice_number?: string; allocated_amount: number }>;
  advance_adjustments: Array<{ advance_voucher_number?: string; invoice_number?: string; allocated_amount: number }>;
  debit_note_adjustments: Array<{ debit_note_number?: string; invoice_number?: string; allocated_amount: number }>;
}

export interface CreatePaymentVoucherPayload {
  voucher_date: string;
  payment_type: PaymentType;
  settlement_scope?: PaymentSettlementScope | string;
  supplier_id?: string;
  employee_id?: string;
  leave_settlement_id?: string;
  payee_name?: string;
  payment_account_id: string;
  destination_account_id?: string;
  amount: number;
  currency?: string;
  exchange_rate?: number;
  payment_mode: PaymentMode;
  reference_number?: string;
  narration?: string;
  cheque_number?: string;
  cheque_date?: string;
  bank_name?: string;
  transaction_reference?: string;
  project_id?: string;
  department_id?: string;
  cost_center?: string;
  branch_id?: string;
  allocations?: { supplier_invoice_id: string; allocated_amount: number }[];
  settlement_lines?: SettlementLineInput[];
  advance_settlement_lines?: { supplier_advance_request_id: string; allocated_amount: number }[];
  discount_amount?: number;
  write_off_amount?: number;
  settlement_remarks?: string;
  direct_lines?: { account_id: string; amount: number; description?: string; project_id?: string; cost_center?: string }[];
  charge_lines?: { account_id: string; amount: number; description?: string; project_id?: string; cost_center?: string }[];
}

export interface PaymentVoucherFilters {
  from_date?: string;
  to_date?: string;
  supplier_id?: string;
  status?: string;
  voucher_number?: string;
  payment_type?: string;
  payment_mode?: string;
  search?: string;
}

export interface OpenSupplierInvoiceForPayment {
  id: string;
  invoice_number: string;
  supplier_invoice_number: string;
  supplier_invoice_date: string;
  received_date: string;
  due_date: string;
  grand_total: number;
  paid_amount: number;
  balance_due: number;
  debit_account_id: string | null;
}

export interface PaymentVoucherPrintDetail {
  voucher_number: string;
  voucher_date: string;
  posting_date: string | null;
  status: string;
  payment_type: string;
  settlement_scope?: PaymentSettlementScope | string;
  payment_mode: string;
  currency: string;
  amount: number;
  discount_amount: number;
  write_off_amount: number;
  settlement_remarks: string | null;
  payee_name: string | null;
  supplier_name: string | null;
  supplier_code: string | null;
  multi_supplier_names?: string[];
  payment_account_code: string | null;
  payment_account_name: string | null;
  destination_account_code: string | null;
  destination_account_name: string | null;
  reference_number: string | null;
  narration: string | null;
  cheque_number: string | null;
  cheque_date: string | null;
  bank_name: string | null;
  transaction_reference: string | null;
  supplier_advance_request_number: string | null;
  advance_settlement_lines?: Array<{
    supplier_advance_request_id: string;
    advance_request_number: string | null;
    allocated_amount: number;
    purchase_order_id: string | null;
  }>;
  purchase_order_number: string | null;
  allocations: Array<{
    invoice_number: string;
    supplier_invoice_number?: string;
    allocated_amount: number;
    grand_total?: number;
    balance_due?: number;
    cash_amount?: number;
    advance_adjustment?: number;
    debit_note_adjustment?: number;
    line_total?: number;
  }>;
  allocation_lines?: PaymentVoucherAllocationLine[];
  advance_application_lines?: PaymentVoucherAdvanceApplication[];
  advance_applications_total?: number;
  direct_lines: Array<{
    account_code: string;
    account_name: string;
    amount: number;
    description?: string;
    project_number?: string;
    project_name?: string;
  }>;
  charge_lines?: Array<{
    account_code: string;
    account_name: string;
    amount: number;
    description?: string;
    project_number?: string;
    project_name?: string;
  }>;
  charges_total?: number;
  total_bank_outflow?: number;
  settlement: PaymentVoucherSettlementHistory | null;
  journal_voucher: {
    voucher_number: string;
    voucher_date: string;
    status: string;
    lines: Array<{
      account_code: string;
      account_name: string;
      description?: string;
      debit_amount: number;
      credit_amount: number;
    }>;
  } | null;
}

export interface PaymentReceiptPrintData {
  voucher_number: string;
  voucher_date: string;
  paid_to: string;
  amount: number;
  currency: string;
  payment_mode: string;
  payment_type: string;
  cheque_number?: string;
  cheque_date?: string;
  bank_name?: string;
  transaction_reference?: string;
  narration?: string;
  allocations: { invoice_number: string; allocated_amount: number }[];
}

class PaymentVoucherService {
  async list(filters?: PaymentVoucherFilters): Promise<PaymentVoucher[]> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([k, v]) => {
        if (v != null && v !== '') params.append(k, String(v));
      });
    }
    const response = await api.get<PaymentVoucher[]>(`/finance/payment-vouchers?${params.toString()}`);
    const data = (response as any)?.data ?? response;
    return Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
  }

  async listEmployeePayables(employeeId?: string): Promise<any[]> {
    const params = new URLSearchParams();
    if (employeeId) params.append('employee_id', employeeId);
    const q = params.toString();
    const response = await api.get(
      `/finance/payment-vouchers/employee-payables${q ? `?${q}` : ''}`
    );
    const data = (response as any)?.data ?? response;
    return Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
  }

  async getById(id: string): Promise<PaymentVoucher> {
    const response = await api.get<PaymentVoucher>(`/finance/payment-vouchers/${id}`);
    const data = (response as any)?.data ?? response;
    return data?.data ?? data;
  }

  async create(payload: CreatePaymentVoucherPayload): Promise<PaymentVoucher> {
    const response = await api.post<PaymentVoucher>('/finance/payment-vouchers', payload);
    const data = (response as any)?.data ?? response;
    return data?.data ?? data;
  }

  async update(id: string, payload: Partial<CreatePaymentVoucherPayload>): Promise<PaymentVoucher> {
    const response = await api.put<PaymentVoucher>(`/finance/payment-vouchers/${id}`, payload);
    const data = (response as any)?.data ?? response;
    return data?.data ?? data;
  }

  async submitForApproval(id: string): Promise<PaymentVoucher> {
    const response = await api.post<PaymentVoucher>(`/finance/payment-vouchers/${id}/submit-for-approval`, {});
    const data = (response as any)?.data ?? response;
    return data?.data ?? data;
  }

  async approveDocument(id: string, remarks?: string): Promise<PaymentVoucher> {
    const response = await api.post<PaymentVoucher>(`/finance/payment-vouchers/${id}/approve-document`, {
      remarks: remarks ?? undefined,
    });
    const data = (response as any)?.data ?? response;
    return data?.data ?? data;
  }

  async rejectApproval(id: string, payload: RejectionPayload): Promise<PaymentVoucher> {
    const response = await api.post<PaymentVoucher>(`/finance/payment-vouchers/${id}/reject-approval`, payload);
    const data = (response as any)?.data ?? response;
    return data?.data ?? data;
  }

  async revokeApproval(id: string, reason: string): Promise<PaymentVoucher> {
    const response = await api.post<PaymentVoucher>(`/finance/payment-vouchers/${id}/revoke-approval`, {
      reason,
    });
    const data = (response as any)?.data ?? response;
    return data?.data ?? data;
  }

  async post(id: string): Promise<PaymentVoucher> {
    const response = await api.post<PaymentVoucher>(`/finance/payment-vouchers/${id}/post`);
    const data = (response as any)?.data ?? response;
    return data?.data ?? data;
  }

  async cancel(id: string): Promise<PaymentVoucher> {
    const response = await api.post<PaymentVoucher>(`/finance/payment-vouchers/${id}/cancel`);
    const data = (response as any)?.data ?? response;
    return data?.data ?? data;
  }

  async getPrintData(id: string): Promise<PaymentVoucherPrintDetail> {
    const response = await api.get<PaymentVoucherPrintDetail>(`/finance/payment-vouchers/${id}/print`);
    const data = (response as any)?.data ?? response;
    return data?.data ?? data;
  }

  /** Data for payment receipt print (for payee signature). */
  async getPrintReceiptData(id: string): Promise<PaymentReceiptPrintData> {
    const response = await api.get<PaymentReceiptPrintData>(`/finance/payment-vouchers/${id}/print-receipt`);
    const data = (response as any)?.data ?? response;
    return data?.data ?? data;
  }

  /** Returns the linked journal voucher for printing (when posted). */
  async getPrintVoucherData(id: string): Promise<any | null> {
    const pv = await this.getById(id);
    if (!pv?.journal_voucher_id) return null;
    const response = await api.get(`/finance/vouchers/${pv.journal_voucher_id}`);
    const data = (response as any)?.data ?? response;
    return data?.data ?? data ?? null;
  }

  async getApSettlementContext(supplierId: string, currency?: string): Promise<ApSettlementContext> {
    const params = new URLSearchParams({ supplier_id: supplierId });
    if (currency) params.append('currency', currency);
    const response = await api.get<ApSettlementContext>(
      `/finance/payment-vouchers/ap-settlement-context?${params.toString()}`
    );
    const data = (response as any)?.data ?? response;
    return data?.data ?? data;
  }

  async getApSettlementContextMulti(
    supplierIds: string[],
    currency?: string
  ): Promise<ApSettlementContextMulti> {
    const params = new URLSearchParams({
      supplier_ids: supplierIds.filter(Boolean).join(','),
    });
    if (currency) params.append('currency', currency);
    const response = await api.get<ApSettlementContextMulti>(
      `/finance/payment-vouchers/ap-settlement-context-multi?${params.toString()}`
    );
    const data = (response as any)?.data ?? response;
    return data?.data ?? data;
  }

  async getAdvanceAllocations(id: string): Promise<PaymentVoucherAdvanceApplication[]> {
    const response = await api.get<PaymentVoucherAdvanceApplication[]>(
      `/finance/payment-vouchers/${id}/advance-allocations`
    );
    const data = (response as any)?.data ?? response;
    return data?.data ?? data ?? [];
  }

  async getSettlementHistory(id: string): Promise<PaymentVoucherSettlementHistory> {
    const response = await api.get<PaymentVoucherSettlementHistory>(
      `/finance/payment-vouchers/${id}/settlement-history`
    );
    const data = (response as any)?.data ?? response;
    return data?.data ?? data;
  }

  async getOpenSupplierInvoices(supplierId: string): Promise<OpenSupplierInvoiceForPayment[]> {
    const response = await api.get<OpenSupplierInvoiceForPayment[]>(
      `/procurement/supplier-invoices/open-for-payment?supplier_id=${encodeURIComponent(supplierId)}`
    );
    const data = (response as any)?.data ?? response;
    const list = data?.data ?? data;
    return Array.isArray(list) ? list : [];
  }
}

export const paymentVoucherService = new PaymentVoucherService();
export default paymentVoucherService;
